import { t } from "@lingui/macro";
import { useNavigation, NavigationProp } from "@react-navigation/native";
import { format, parse } from "date-fns";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { HoleCount } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { PeopleFillIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { useClaimSlotForRequest } from "./hooks/useClaimSlotForRequest";

type AvailableTeeTimeRowProps = {
  slotId: string | null;
  time: string | null;
  courseName?: string | null;
  shouldShowCourseName: boolean;
  supportsPublic: boolean;
  groupSize: number;
  bookingId: string;
  holeCount: HoleCount;
  slotRates: {
    nineHolePrice: number;
    eighteenHolePrice: number;
    rate: {
      name: string;
    };
  }[];
};

export const AvailableTeeTimeRow = ({
  slotId,
  bookingId,
  groupSize,
  time,
  courseName,
  shouldShowCourseName,
  supportsPublic,
  holeCount,
  slotRates,
}: AvailableTeeTimeRowProps) => {
  const [claimSlotForRequest, isClaimingSlotForRequest] =
    useClaimSlotForRequest();
  const { currencySymbol } = useGetClubCurrency();

  const formattedTime = time
    ? format(parse(time, "HH:mm:ss", new Date()), "h:mm aaa")
    : "";

  const firstSlotRate = slotRates?.[0] ?? null;

  const navigation =
    useNavigation<
      NavigationProp<ClubStackNavigatorParamList, "BookingSummary">
    >();

  const onComplete = (result: {
    success: boolean;
    draftBookingId?: string | null;
    targetSlot?: unknown;
    errors?: { code: string; field?: string | null }[];
  }) => {
    if (result.success) {
      navigation.navigate("BookingSummary", {
        slotOrBookingId: result.draftBookingId ?? "",
        params: {
          currentRequestBookingId: bookingId,
        },
      });
    }
  };

  const handleSlotPress = () => {
    if (slotId) {
      claimSlotForRequest(
        { requestBookingId: bookingId, targetSlotId: slotId },
        (result) => {
          onComplete(result);
        }
      );
    }
  };

  return (
    <Pressable onPress={handleSlotPress}>
      <HStack
        padding={4}
        justifyContent="space-between"
        backgroundColor="surface-primary"
        alignItems="center"
        position="relative"
        width="100%"
        borderRadius="2"
        overflow="hidden"
      >
        {isClaimingSlotForRequest ? (
          <LoadingSpinner width={32} height={32} />
        ) : null}
        <Box gap={1}>
          <HStack gap={1} alignItems="center">
            <Text variant="mainBaseSemiBold">{formattedTime}</Text>
            {shouldShowCourseName && courseName ? (
              <Text color="text-secondary" variant="mainTiny">
                {courseName}
              </Text>
            ) : null}
          </HStack>
          <HStack gap={2}>
            <PeopleFillIcon />
            <Text variant="mainTiny">{t`Up to ${groupSize}`}</Text>
          </HStack>
        </Box>

        {supportsPublic ? (
          <Box gap={1} alignItems="flex-end">
            <Text color="text-main-primary" variant="mainBaseSemiBold">
              {`${currencySymbol}${ConvertCentsToDollars(
                holeCount === "NINE"
                  ? firstSlotRate?.nineHolePrice ?? 0
                  : firstSlotRate?.eighteenHolePrice ?? 0
              )}`}
            </Text>
            <Text color="text-secondary" variant="mainTiny">
              {firstSlotRate?.rate?.name ?? ""}
            </Text>
          </Box>
        ) : null}
      </HStack>
    </Pressable>
  );
};
